import { Component } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";

/**
 * RouteErrorBoundary — last line of defence against a single component
 * crash white-screening the whole app.
 *
 * Why class component? React's error-boundary contract still requires
 * `componentDidCatch` / `getDerivedStateFromError`, neither of which
 * have function-component equivalents.
 *
 * The fallback UI gives the user three escape hatches:
 *   1. "Reload page" — for transient errors (network blips, hot-reload
 *      missing a chunk).
 *   2. "Back to projects" — when the route itself is broken and the
 *      user just wants to keep working.
 *   3. The actual error message + component stack (only when
 *      ?debug=1 is on the URL or REACT_APP_DEBUG_BOUNDARY is set) so
 *      developers can diagnose without opening DevTools on a phone.
 *
 * Iter-118.1 — added after a deployment-time `ShoppingBag is not
 * defined` ReferenceError in ProjectHome white-screened the app.
 */
export default class RouteErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[RouteErrorBoundary]", error, info);
        this.setState({ info });
    }

    handleReload = () => {
        // Hard reload, blow away in-memory chunk cache.
        if (typeof window !== "undefined") window.location.reload();
    };

    render() {
        if (!this.state.error) return this.props.children;

        const debug =
            (typeof window !== "undefined" &&
                window.location?.search?.includes("debug=1")) ||
            process.env.REACT_APP_DEBUG_BOUNDARY === "1";

        const message = String(this.state.error?.message || this.state.error);

        return (
            <div
                data-testid="route-error-boundary"
                className="min-h-screen flex items-center justify-center px-5 bg-background text-foreground"
            >
                <div className="max-w-md w-full rounded-xl border border-white/10 bg-card p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-amber-400/15 text-amber-300 flex items-center justify-center shrink-0">
                            <AlertTriangle size={18} />
                        </div>
                        <div>
                            <h1 className="font-display text-xl font-bold leading-tight">
                                Something broke on this screen
                            </h1>
                            <p className="text-xs text-neutral-400 mt-0.5">
                                The rest of the app is still working.
                            </p>
                        </div>
                    </div>

                    {debug ? (
                        <pre
                            data-testid="route-error-boundary-debug"
                            className="text-[11px] whitespace-pre-wrap break-words rounded-md border border-white/10 bg-black/40 p-3 max-h-48 overflow-y-auto text-amber-200"
                        >
                            {message}
                            {this.state.info?.componentStack ? `\n\n${this.state.info.componentStack}` : ""}
                        </pre>
                    ) : null}

                    <div className="flex flex-col sm:flex-row gap-2 pt-1">
                        <button
                            type="button"
                            data-testid="route-error-reload"
                            onClick={this.handleReload}
                            className="instant-tap min-tap inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground font-semibold px-4 py-2"
                        >
                            <RefreshCw size={14} />
                            Reload page
                        </button>
                        <Link
                            to="/"
                            data-testid="route-error-home"
                            className="instant-tap min-tap inline-flex items-center justify-center gap-2 rounded-md bg-secondary border border-white/10 text-foreground px-4 py-2"
                        >
                            <Home size={14} />
                            Back to projects
                        </Link>
                    </div>
                </div>
            </div>
        );
    }
}
